import axios from 'axios'
import userStorage from '../utils/userStorage';
import tokenService from './tokenService';
import { defaultConfig, getNonAuthRequiredRoutes } from './apiConfig';
import { validateId, formatId } from '../utils/validation';
import { CONNECTION_STATES } from '../contexts/authConstants';

// Utility function to format ID in URL
const formatUrlWithId = (url, id) => {
  try {
    // Validate and format the ID
    validateId(id, { allowLegacy: true });
    return url.replace(':id', formatId(id));
  } catch (error) {
    console.error('Invalid ID in URL:', error);
    throw error;
  }
};

// Utility function to unwrap response data
const unwrapResponseData = (response) => {
  // First check for error responses
  if (response?.data?.err || response?.data?.error) {
    const errorMessage = response.data.err || response.data.error;
    // If it's a string, throw it directly
    if (typeof errorMessage === 'string') {
      throw new Error(errorMessage);
    }
    // If it's an object with a message property
    if (errorMessage.message) {
      throw new Error(errorMessage.message);
    }
    // If it's some other error format
    throw new Error(JSON.stringify(errorMessage));
  }

  // Then handle nested data
  if (response?.data?.data !== undefined && response?.data?.data !== null) {
    return response.data.data;
  }

  // Otherwise return the data property as is
  return response?.data;
};

// set base url, and headers for interceptors
const api = axios.create(defaultConfig);

// Track refresh token attempts
let isRefreshing = false;
let failedQueue = [];
// ... response interceptor ...

// Get excluded URLs from the configuration
const excludedUrls = getNonAuthRequiredRoutes();

const isExcludedUrl = (url) => excludedUrls.some((excludedUrl) => url.includes(excludedUrl));

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor with UUID validation
api.interceptors.request.use(
  async config => {
    // Add authorization header if needed
    if (!isExcludedUrl(config.url)) {
      const token = tokenService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Handle URL parameters for UUIDs
    if (config.params) {
      const newParams = { ...config.params };
      // Convert userId to id if present
      if (newParams.userId && !newParams.id) {
        newParams.id = newParams.userId;
        delete newParams.userId;
      }
      // Validate any ID parameters
      if (newParams.id) {
        validateId(newParams.id, { allowLegacy: true });
      }
      config.params = newParams;
    }

    // Handle URL path parameters
    if (config.url) {
      const idMatch = config.url.match(/\/(\d+|[0-9a-f-]{36})(\/|$)/i);
      if (idMatch) {
        try {
          validateId(idMatch[1], { allowLegacy: true });
        } catch (error) {
          console.error('Invalid ID in URL:', error);
          throw error;
        }
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  response => {
    // Blob responses (images, audio, file downloads) should not be processed
    // by the JSON unwrapping/transformation logic
    if (response.data instanceof Blob) {
      return response;
    }

    try {
      // Preserve meta property before unwrapping (needed for pagination)
      const originalMeta = response?.data?.meta;

      // Preserve pagination fields if they exist (for endpoints that return { data: [...], total, page, totalPages, limit })
      // These are preserved at the response level, not in response.data, to avoid breaking existing code
      const paginationInfo = {};
      if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        if ('total' in response.data) paginationInfo.total = response.data.total;
        if ('page' in response.data) paginationInfo.page = response.data.page;
        if ('totalPages' in response.data) paginationInfo.totalPages = response.data.totalPages;
        if ('limit' in response.data) paginationInfo.limit = response.data.limit;
      }

      // Transform response data
      const data = unwrapResponseData(response);

      // Handle user ID transformations in response data
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        // Convert userId to id if needed
        if (data.userId && !data.id) {
          data.id = data.userId;
          data.legacyId = data.userId.toString();
          delete data.userId;
        }

        // Ensure ID is properly formatted
        if (data.id) {
          try {
            validateId(data.id, { allowLegacy: true });
          } catch (error) {
            console.error('Invalid ID in response:', error);
          }
        }
      }

      // Return response with unwrapped data, preserved meta, and pagination info
      // Pagination info is attached to response object (not response.data) to avoid breaking existing code
      return {
        ...response,
        data,
        meta: originalMeta,
        ...(Object.keys(paginationInfo).length > 0 ? { pagination: paginationInfo } : {})
      };
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async error => {
    const originalRequest = error.config;

    // Check for error response with err/error properties
    if (error.response?.data?.err || error.response?.data?.error) {
      const errorData = error.response.data.err || error.response.data.error;
      if (typeof errorData === 'string') {
        error.message = errorData;
      } else if (errorData.message) {
        error.message = errorData.message;
      } else {
        error.message = JSON.stringify(errorData);
      }
    }

    // Handle 401 and token refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isExcludedUrl(originalRequest.url)) {
      // If we're offline and have a refresh token, just set offline state without triggering auth error
      if (!navigator.onLine && tokenService.hasRefreshToken()) {
        localStorage.setItem('connection_state', CONNECTION_STATES.OFFLINE);
        window.dispatchEvent(new CustomEvent('connection-state-change', {
          detail: { state: CONNECTION_STATES.OFFLINE }
        }));
        // Return a special offline error
        const offlineError = new Error('Device is offline');
        offlineError.isOffline = true;
        return Promise.reject(offlineError);
      }

      // Dispatch event to signal token refresh is starting
      window.dispatchEvent(new CustomEvent('token-refresh-start'));

      if (isRefreshing) {
        try {
          const token = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (err) {
          // Dispatch event to signal token refresh has ended with failure
          window.dispatchEvent(new CustomEvent('token-refresh-end', {
            detail: { success: false, error: err }
          }));
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt token refresh — tokenService handles its own timeout (10s) and
        // cross-tab deduplication. Do NOT wrap in retryWithBackoff as that would
        // multiply the maximum blocking time (10s × 3 retries = up to 33s).
        const refreshResult = navigator.onLine
          ? await tokenService.refreshAccessToken()
          : { offline: true, message: 'Device is offline, using cached credentials' };

        // Handle offline mode
        if (refreshResult.offline) {
          processQueue(new Error('Offline mode'), null);
          // Set offline state
          localStorage.setItem('connection_state', CONNECTION_STATES.OFFLINE);
          window.dispatchEvent(new CustomEvent('connection-state-change', {
            detail: { state: CONNECTION_STATES.OFFLINE }
          }));

          // Dispatch event to signal token refresh has ended
          window.dispatchEvent(new CustomEvent('token-refresh-end', {
            detail: { success: false, offline: true }
          }));

          // Return a special offline error
          const offlineError = new Error('Device is offline');
          offlineError.isOffline = true;
          return Promise.reject(offlineError);
        }

        // Normal online flow - success
        processQueue(null, refreshResult.access_token);
        originalRequest.headers.Authorization = `Bearer ${refreshResult.access_token}`;

        // Dispatch event to signal successful token refresh
        window.dispatchEvent(new CustomEvent('token-refresh-end', {
          detail: { success: true }
        }));

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Dispatch event to signal token refresh has failed
        window.dispatchEvent(new CustomEvent('token-refresh-end', {
          detail: { success: false, error: refreshError }
        }));

        // Handle network errors by setting offline state
        if (!navigator.onLine || refreshError.message?.includes('Network Error') ||
          refreshError.message?.includes('timeout') || refreshError.code === 'ECONNABORTED') {
          localStorage.setItem('connection_state', CONNECTION_STATES.OFFLINE);
          window.dispatchEvent(new CustomEvent('connection-state-change', {
            detail: { state: CONNECTION_STATES.OFFLINE }
          }));
        } else {
          // Only clear tokens and dispatch auth error if it's not a network error
          tokenService.deleteAccessToken();
          const isTimeout = refreshError.message?.includes('timeout');
          window.dispatchEvent(new CustomEvent('auth-error', {
            detail: {
              message: isTimeout
                ? 'Your session took too long to refresh. Please log in again.'
                : 'Session expired. Please log in again.'
            }
          }));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Helper methods for common API operations
const apiHelpers = {
  /**
   * Get a resource by ID
   * @param {string} endpoint - The API endpoint
   * @param {string|number} id - The resource ID (UUID or legacy ID)
   * @returns {Promise} API response
   */
  getById: async (endpoint, id) => {
    validateId(id, { allowLegacy: true });
    return api.get(`${endpoint}/${formatId(id)}`);
  },

  /**
   * Update a resource by ID
   * @param {string} endpoint - The API endpoint
   * @param {string|number} id - The resource ID (UUID or legacy ID)
   * @param {Object} data - The update data
   * @returns {Promise} API response
   */
  updateById: async (endpoint, id, data) => {
    validateId(id, { allowLegacy: true });
    return api.put(`${endpoint}/${formatId(id)}`, data);
  },

  /**
   * Delete a resource by ID
   * @param {string} endpoint - The API endpoint
   * @param {string|number} id - The resource ID (UUID or legacy ID)
   * @returns {Promise} API response
   */
  deleteById: async (endpoint, id) => {
    validateId(id, { allowLegacy: true });
    return api.delete(`${endpoint}/${formatId(id)}`);
  }
};

export { apiHelpers };
export default api;